import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// The first render of the document is a full load and the browser has
// handled focus. Module state, not a ref, so that a remount (the error page
// swapping in and out) or a return to the first history entry still counts
// as navigation.
let handledFirstLoad = false;

/** Test hook: behave as if the document had just loaded. */
export const resetRouteFocusForTests = () => {
  handledFirstLoad = false;
};

const hashTarget = (hash: string) => {
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null; // malformed percent-encoding in a crafted URL
  }
};

/**
 * Client-side navigation swaps the page content without the focus reset and
 * "new document" announcement a full page load gives assistive technology.
 * After each navigation this moves keyboard focus to the hash target when
 * there is one, otherwise to the main content, so screen readers announce
 * the new page and Tab continues from the top of it rather than from the
 * link that was clicked (often in the footer of the previous page).
 */
const RouteFocus = () => {
  const { key, hash } = useLocation();

  useEffect(() => {
    if (!handledFirstLoad) {
      handledFirstLoad = true;
      return;
    }
    const target =
      (hash ? hashTarget(hash) : null) ?? document.getElementById("content");
    if (!target) return;
    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
    }
    target.focus({ preventScroll: true });
  }, [key, hash]);

  return null;
};

export default RouteFocus;
